'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getBlogExcerpt } from '@eliadmin/core/src/utils/blog-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { ComponentProps } from 'react';
import React from 'react';

export type BlogCardProps = Omit<ComponentProps<typeof Card>, 'className'> & {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
};

export function BlogCard({ blog, className, ...props }: BlogCardProps) {
  const textContent = getBlogExcerpt(blog.content);
  const locale = useLocaleStore((state) => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Card
      {...props}
      className={cn('relative w-[386px] max-w-[90vw]', 'ea-blog-card', className?.main)}
    >
      <Link
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug }) ?? '#'}
        className={cn('ea-blog-card__link', className?.link)}
        prefetch={false}
      >
        <CardContent className={cn('p-0', 'ea-blog-card__content', className?.contentContainer)}>
          <Image
            alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
            src={blog.image?.url || '/images/placeholder.png'}
            width={0}
            height={0}
            sizes="100vw"
            className={cn(
              'h-[220px] w-full rounded-t-[10px] object-cover object-center',
              'ea-blog-card__image',
              className?.image,
            )}
          />
          <div
            className={cn(
              'flex flex-col space-y-4 p-4 pt-0 lg:p-6 lg:pt-0',
              'ea-blog-card__content-container',
              className?.contentContainer,
            )}
          >
            <Badge
              size={'md'}
              className={cn(
                'pointer-events-none flex flex-grow-0 items-center gap-1 rounded-md bg-gray-100 px-2 text-xs font-semibold leading-5 text-foreground opacity-80',
                'ea-blog-card__date-badge',
                'absolute left-4 top-4',
                className,
              )}
            >
              {React.cloneElement(BLOGS_CONFIG.blogCard.calendarIcon as any, {
                className: cn('ea-availability-label__icon'),
              })}
              {formattedDate}
            </Badge>

            <h3
              data-ea-visual-tag="h6"
              className={cn(
                'line-clamp-2 h-12 text-ellipsis font-bold leading-6',
                'ea-blog-card__title',
                className?.title,
              )}
            >
              {blog.name}
            </h3>
            <p
              className={cn(
                'line-clamp-3 h-16 text-ellipsis text-sm leading-5 text-muted-foreground',
                'ea-blog-card__content',
                className?.content,
              )}
            >
              {textContent}
            </p>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
